/**
 * Provider Agreement Component
 * 30-page legal document with digital signature pad + typed confirmation
 * Indiana compliant electronic signatures
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, Check, ChevronDown, ChevronUp, AlertTriangle,
  PenTool, Loader2, Shield, Clock, Download, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signature pad component
function SignaturePad({ onSignatureChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = 150;
    
    // Set drawing style
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Fill white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      if (hasSignature) {
        const canvas = canvasRef.current;
        const dataUrl = canvas.toDataURL('image/png');
        onSignatureChange(dataUrl);
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange(null);
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <PenTool className="w-4 h-4" />
        Draw Your Signature
      </Label>
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-500">Sign above using mouse or touch</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={clearSignature}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

export default function ProviderAgreement({ onComplete, providerId }) {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [initiatedSections, setInitiatedSections] = useState({});
  const [signatureImage, setSignatureImage] = useState(null);
  const [typedSignature, setTypedSignature] = useState('');
  const [fullLegalName, setFullLegalName] = useState(user?.name || '');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  // Fetch agreement
  useEffect(() => {
    fetchAgreement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAgreement = async () => {
    setLoading(true);
    try {
      // NOTE: explicitly NOT using withCredentials here. The legal/agreement
      // endpoint is public + read-only and including credentials triggers a
      // CORS rejection when the edge proxy returns `Access-Control-Allow-Origin: *`.
      const response = await axios.get(`${API}/legal/agreement`, {
        headers: getAuthHeaders(),
      });
      setAgreement(response.data);
    } catch (error) {
      console.error('Agreement load failed:', error?.response?.status, error?.response?.data || error?.message);
      const msg = error?.response?.data?.detail
        || (error?.message?.includes('Network') ? 'Network error — check your connection' : 'Failed to load agreement');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleInitial = (sectionId) => {
    setInitiatedSections(prev => ({
      ...prev,
      [sectionId]: true
    }));
    
    // Auto-expand next section
    const currentIndex = agreement.sections.findIndex(s => s.section_id === sectionId);
    if (currentIndex < agreement.sections.length - 1) {
      const nextSection = agreement.sections[currentIndex + 1];
      setExpandedSections(prev => ({
        ...prev,
        [nextSection.section_id]: true
      }));
      setCurrentSection(currentIndex + 1);
    }
  };

  const allSectionsInitialed = () => {
    if (!agreement) return false;
    return agreement.requires_initials_on.every(id => initiatedSections[id]);
  };

  const canSign = () => {
    return (
      allSectionsInitialed() &&
      signatureImage &&
      typedSignature &&
      typedSignature.toLowerCase() === fullLegalName.toLowerCase() &&
      agreedToTerms
    );
  };

  const handleSign = async () => {
    if (!canSign()) {
      toast.error('Please complete all required fields');
      return;
    }

    setSigning(true);
    try {
      const response = await axios.post(
        `${API}/legal/agreement/sign`,
        {
          provider_id: providerId || user?.user_id,
          full_legal_name: fullLegalName,
          typed_signature: typedSignature,
          signature_image: signatureImage,
          ip_address: null, // Server will capture
          user_agent: navigator.userAgent,
          agreement_version: agreement.version,
          sections_initialed: Object.keys(initiatedSections).filter(k => initiatedSections[k])
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      toast.success('Agreement signed successfully!');
      
      if (onComplete) {
        onComplete(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign agreement');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!agreement) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p>Failed to load agreement. Please try again.</p>
          <Button onClick={fetchAgreement} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const progress = Math.round(
    (Object.values(initiatedSections).filter(Boolean).length / agreement.requires_initials_on.length) * 100
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="provider-agreement">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-xl">Independent Contractor Agreement</CardTitle>
              <CardDescription>
                Version {agreement.version} • Effective {agreement.effective_date} • {agreement.total_pages} pages
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span>Reading Progress</span>
              <span>{progress}% complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Important Notice */}
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-800">Important Legal Document</p>
              <p className="text-amber-700 mt-1">
                Please read each section carefully. This agreement contains important provisions regarding 
                non-competition, profit sharing, and intellectual property. You are encouraged to seek 
                independent legal counsel before signing.
              </p>
            </div>
          </div>

          {/* Indiana Compliance Badge */}
          <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg mb-6">
            <Shield className="w-5 h-5 text-blue-600" />
            <div className="text-sm">
              <p className="font-medium text-blue-800">Indiana E-Sign Compliant</p>
              <p className="text-blue-600 text-xs">{agreement.indiana_compliance.statute}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agreement Sections */}
      <div className="space-y-3">
        {agreement.sections.map((section, index) => (
          <Card 
            key={section.section_id}
            className={`transition-all ${
              section.critical ? 'border-red-300 bg-red-50/30' : ''
            } ${
              initiatedSections[section.section_id] ? 'border-green-300' : ''
            }`}
          >
            <CardHeader 
              className="cursor-pointer"
              onClick={() => toggleSection(section.section_id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">Pages {section.page_range}</span>
                  <h3 className="font-semibold">{section.title}</h3>
                  {section.critical && (
                    <Badge className="bg-red-500">Critical</Badge>
                  )}
                  {initiatedSections[section.section_id] && (
                    <Check className="w-5 h-5 text-green-600" />
                  )}
                </div>
                {expandedSections[section.section_id] ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </CardHeader>

            {expandedSections[section.section_id] && (
              <CardContent>
                {/* Section Content */}
                <div className="prose prose-sm max-w-none mb-6">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                    {section.content}
                  </pre>
                </div>

                {/* Initial Button */}
                {section.requires_initial && (
                  <div className="flex items-center justify-between pt-4 border-t">
                    {initiatedSections[section.section_id] ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <Check className="w-5 h-5" />
                        <span>Initialed</span>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">
                          Click to confirm you have read and understood this section
                        </p>
                        <Button
                          onClick={() => handleInitial(section.section_id)}
                          variant="outline"
                          data-testid={`initial-${section.section_id}`}
                        >
                          I Have Read This Section
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Signature Section */}
      {allSectionsInitialed() && (
        <Card className="border-2 border-blue-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Sign Agreement
            </CardTitle>
            <CardDescription>
              Complete your digital signature below
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Full Legal Name */}
            <div>
              <Label htmlFor="fullName">Full Legal Name</Label>
              <Input
                id="fullName"
                value={fullLegalName}
                onChange={(e) => setFullLegalName(e.target.value)}
                placeholder="Enter your full legal name"
                className="mt-1"
                data-testid="full-legal-name"
              />
            </div>

            {/* Signature Pad */}
            <SignaturePad onSignatureChange={setSignatureImage} />

            {/* Typed Signature Confirmation */}
            <div>
              <Label htmlFor="typedSignature">
                Type Your Full Legal Name to Confirm
              </Label>
              <Input
                id="typedSignature"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder={`Type "${fullLegalName}" exactly`}
                className="mt-1"
                data-testid="typed-signature"
              />
              {typedSignature && typedSignature.toLowerCase() !== fullLegalName.toLowerCase() && (
                <p className="text-red-500 text-sm mt-1">
                  Typed name must match your full legal name exactly
                </p>
              )}
            </div>

            {/* Agreement Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <Checkbox
                id="agree"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                data-testid="agree-checkbox"
              />
              <label htmlFor="agree" className="text-sm text-gray-700 cursor-pointer">
                I acknowledge that I have read and understood all {agreement.total_pages} pages of this 
                Independent Contractor Agreement. I understand the implications of the non-competition 
                provisions, including the 20% profit share requirement for business imitation 
                (Article VII, Section 7.4). I am signing this agreement voluntarily and have had 
                the opportunity to consult with legal counsel.
              </label>
            </div>

            {/* Sign Button */}
            <Button
              onClick={handleSign}
              disabled={!canSign() || signing}
              className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
              data-testid="sign-agreement-btn"
            >
              {signing ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <PenTool className="w-5 h-5 mr-2" />
              )}
              Sign Agreement
            </Button>

            {/* Timestamp Note */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>Your signature will be timestamped and securely stored</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help Text */}
      {!allSectionsInitialed() && (
        <div className="text-center text-sm text-gray-500">
          <p>Please read and initial all {agreement.requires_initials_on.length} sections to proceed with signing.</p>
          <p className="mt-1">
            {agreement.requires_initials_on.length - Object.values(initiatedSections).filter(Boolean).length} sections remaining
          </p>
        </div>
      )}
    </div>
  );
}
